import { useEffect, useState } from "react";
import { ChevronDown, ArrowRight, Menu, X } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { SECTIONS } from "../../data/cottages";
import { LangSwitch } from "./LangSwitch";
import { Sprig } from "./Ornaments";
import { headerOffset, scrollToId } from "./scroll";

const useScrollSpy = () => {
  const [active, setActive] = useState("hem");
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY + headerOffset() + 40;
        let current = "hem";
        SECTIONS.forEach(({ id }) => {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top + window.scrollY <= y) current = id;
        });
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) current = "kontakt";
        setActive(current);
        setScrolled(window.scrollY > 12);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return { active, scrolled };
};

export const Wordmark = ({ testId, onClick, light = false }) => {
  const { t } = useLang();
  return (
    <a href="#hem" className={`wordmark ${light ? "wordmark--light" : ""}`} onClick={onClick} data-testid={testId}>
      <Sprig className="wordmark__sprig" />
      <span className="wordmark__text">
        <span className="wordmark__name">Vimmerbyferie</span>
        <span className="wordmark__tag">{t.brandTag}</span>
      </span>
    </a>
  );
};

const CottageQuickLinks = ({ prefix }) => {
  const { t } = useLang();
  return (
    <div className="nav-cottages" aria-label={t.nav.cottages}>
      <a href="/stugor/karlstugan" className="nav-cottage-link" data-testid={`${prefix}-cottage-karlstugan`}>
        <span className="nav-cottage-link__image">
          <img src="/images/karlstugan/karlstugan-01-thumb.webp" alt="" loading="lazy" />
        </span>
        <span className="nav-cottage-link__copy">
          <strong>Karlstugan Cottage</strong>
          <small>{t.karlstugan.tagline}</small>
        </span>
        <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
      </a>
      <a href="/stugor/bjorkslingan" className="nav-cottage-link" data-testid={`${prefix}-cottage-bjorkslingan`}>
        <span className="nav-cottage-link__image">
          <img src="/images/bjorkslingan/bjorkslingan-05-thumb.webp" alt="" loading="lazy" />
        </span>
        <span className="nav-cottage-link__copy">
          <strong>Björkslingan</strong>
          <small>{t.bjorkslingan.tagline}</small>
        </span>
        <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
      </a>
    </div>
  );
};

const NavLinks = ({ active, onGo, prefix }) => {
  const { t } = useLang();
  return SECTIONS.map(({ id, key }) => {
    const link = (
      <a
        key={id}
        href={`#${id}`}
        className={`nav-link ${id === "stugor" ? "nav-link--cottages" : ""} ${active === id ? "is-active" : ""}`}
        aria-current={active === id ? "location" : undefined}
        onClick={(e) => onGo(e, id)}
        data-testid={`${prefix}-nav-${id}`}
      >
        {t.nav[key]}
        {id === "stugor" && <ChevronDown className="nav-link__chevron" size={14} strokeWidth={1.5} aria-hidden="true" />}
      </a>
    );

    if (id !== "stugor") return link;

    return (
      <div className="nav-item nav-item--cottages" key={id}>
        {link}
        <CottageQuickLinks prefix={prefix} />
      </div>
    );
  });
};

export const Header = () => {
  const { t } = useLang();
  const { active, scrolled } = useScrollSpy();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onResize = () => window.innerWidth >= 900 && setOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    requestAnimationFrame(() => scrollToId(id));
  };

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""} ${open ? "is-open" : ""}`} data-testid="site-header">
      <div className="wrap site-header__bar">
        <Wordmark testId="header-wordmark" onClick={(e) => go(e, "hem")} />
        <nav className="site-nav" aria-label={t.nav.main} data-testid="desktop-nav">
          <NavLinks active={active} onGo={go} prefix="desktop" />
        </nav>
        <div className="site-header__lang">
          <LangSwitch idPrefix="header" />
        </div>
        <button
          type="button"
          className="menu-btn"
          aria-expanded={open}
          aria-controls="mobile-panel"
          aria-label={open ? t.nav.closeMenu : t.nav.menu}
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-button"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          <span className="menu-btn__label">{open ? t.nav.closeMenu : t.nav.menu}</span>
        </button>
      </div>
      <div id="mobile-panel" className="mobile-panel" hidden={!open} data-testid="mobile-menu-panel">
        <nav className="mobile-panel__nav" aria-label={t.nav.main}>
          <NavLinks active={active} onGo={go} prefix="mobile" />
        </nav>
        <div className="mobile-panel__lang">
          <span>{t.nav.language}</span>
          <LangSwitch idPrefix="mobile" />
        </div>
      </div>
    </header>
  );
};
